import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { AlertCircle, CheckCircle2, AlertTriangle, Shield, Clock, Globe } from "lucide-react";
// Toast notifications - using shadcn/ui pattern
const useToast = () => ({
  toast: ({ title, description, variant }: any) => {
    console.log(`[${variant || 'default'}] ${title}: ${description}`);
  },
});

interface AbuseAlert {
  userId: number;
  userName?: string;
  userEmail?: string;
  patterns: Array<{
    patternType: string;
    severity: string;
    abuseScore: number;
    details: Record<string, any>;
  }>;
  totalAbuseScore: number;
  recommendedAction: string;
  createdAt: Date;
}

export default function AdminAbuseAlerts() {
  const { toast } = useToast();
  const [selectedAlert, setSelectedAlert] = useState<AbuseAlert | null>(null);
  const [blockReason, setBlockReason] = useState("");
  const [blockDuration, setBlockDuration] = useState<"1h" | "24h" | "7d" | "permanent">("permanent");
  const [isBlocking, setIsBlocking] = useState(false);

  // Fetch abuse alerts (simulated - would fetch from backend)
  const [alerts, setAlerts] = useState<AbuseAlert[]>([]);

  // Block user mutation
  const blockUserMutation = trpc.admin.smsAbuseAlerts.blockUserSMS.useMutation({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "User blocked successfully",
      });
      setSelectedAlert(null);
      setBlockReason("");
      setIsBlocking(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to block user",
        variant: "destructive",
      });
      setIsBlocking(false);
    },
  });

  // Unblock user mutation
  const unblockUserMutation = trpc.admin.smsAbuseAlerts.unblockUserSMS.useMutation({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "User unblocked successfully",
      });
      setSelectedAlert(null);
      setIsBlocking(false);
    },
    onError: (error: any) => {
      toast({
        title: "Error",
        description: error?.message || "Failed to unblock user",
        variant: "destructive",
      });
      setIsBlocking(false);
    },
  });

  // Reset counters mutation
  const resetCountersMutation = trpc.admin.smsAbuseAlerts.resetUserCounters.useMutation({
    onSuccess: () => {
      toast({
        title: "Success",
        description: "SMS counters reset successfully",
      });
      setSelectedAlert(null);
      setIsBlocking(false);
    },
    onError: (error) => {
      toast({
        title: "Error",
        description: error.message || "Failed to reset counters",
        variant: "destructive",
      });
      setIsBlocking(false);
    },
  });

  const handleBlockUser = async () => {
    if (!selectedAlert || !blockReason.trim()) {
      toast({
        title: "Error",
        description: "Please provide a reason for blocking",
        variant: "destructive",
      });
      return;
    }

    setIsBlocking(true);
    await blockUserMutation.mutateAsync({
      userId: selectedAlert.userId,
      reason: blockReason,
      duration: blockDuration,
    });
  };

  const handleUnblockUser = async () => {
    if (!selectedAlert) return;

    setIsBlocking(true);
    await unblockUserMutation.mutateAsync({
      userId: selectedAlert.userId,
      reason: "Admin unblock",
    });
  };

  const handleResetCounters = async () => {
    if (!selectedAlert) return;

    setIsBlocking(true);
    await resetCountersMutation.mutateAsync({
      userId: selectedAlert.userId,
      reason: "Admin reset",
    });
  };

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "bg-red-100 text-red-800 border-red-300";
      case "high":
        return "bg-orange-100 text-orange-800 border-orange-300";
      case "medium":
        return "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "low":
        return "bg-blue-100 text-blue-800 border-blue-300";
      default:
        return "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case "block":
        return "destructive";
      case "investigate":
        return "secondary";
      case "warn":
        return "outline";
      case "monitor":
        return "secondary";
      default:
        return "outline";
    }
  };

  const getPatternIcon = (patternType: string) => {
    switch (patternType) {
      case "high_frequency":
        return <Clock className="w-4 h-4" />;
      case "country_switch":
        return <Globe className="w-4 h-4" />;
      case "rate_limit_abuse":
        return <AlertTriangle className="w-4 h-4" />;
      case "suspicious_activity":
        return <AlertCircle className="w-4 h-4" />;
      default:
        return <AlertCircle className="w-4 h-4" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">SMS Abuse Alerts</h1>
        <p className="text-gray-600 mt-2">Monitor and manage suspicious SMS activity</p>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Critical Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {alerts.filter((a) => a.totalAbuseScore >= 200).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">High Risk</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-orange-600">
              {alerts.filter((a) => a.totalAbuseScore >= 150 && a.totalAbuseScore < 200).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Medium Risk</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">
              {alerts.filter((a) => a.totalAbuseScore >= 100 && a.totalAbuseScore < 150).length}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600">Total Alerts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{alerts.length}</div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts List */}
      <Card>
        <CardHeader>
          <CardTitle>Detected Abuse Patterns</CardTitle>
          <CardDescription>Click on an alert to view details and take action</CardDescription>
        </CardHeader>
        <CardContent>
          {alerts.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle2 className="w-12 h-12 text-green-600 mx-auto mb-4" />
              <p className="text-gray-600">No abuse alerts detected</p>
            </div>
          ) : (
            <div className="space-y-3">
              {alerts.map((alert) => (
                <div
                  key={alert.userId}
                  className="border rounded-lg p-4 hover:bg-gray-50 cursor-pointer transition-colors"
                  onClick={() => setSelectedAlert(alert)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <h3 className="font-semibold">
                          {alert.userName || `User ${alert.userId}`}
                        </h3>
                        <Badge
                          className={`${getSeverityColor(
                            alert.totalAbuseScore >= 200
                              ? "critical"
                              : alert.totalAbuseScore >= 150
                                ? "high"
                                : alert.totalAbuseScore >= 100
                                  ? "medium"
                                  : "low"
                          )}`}
                        >
                          Score: {alert.totalAbuseScore}
                        </Badge>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{alert.userEmail}</p>

                      {/* Detected Patterns */}
                      <div className="flex flex-wrap gap-2 mb-2">
                        {alert.patterns.map((pattern, idx) => (
                          <div
                            key={idx}
                            className={`flex items-center gap-1 px-2 py-1 rounded text-xs ${getSeverityColor(pattern.severity)}`}
                          >
                            {getPatternIcon(pattern.patternType)}
                            <span className="capitalize">
                              {pattern.patternType.replace(/_/g, " ")}
                            </span>
                          </div>
                        ))}
                      </div>

                      {/* Recommended Action */}
                      <div className="flex items-center gap-2">
                        <Shield className="w-4 h-4 text-gray-500" />
                        <span className="text-sm">
                          Recommended: <span className="font-semibold capitalize">{alert.recommendedAction}</span>
                        </span>
                      </div>
                    </div>
                    <Button
                      variant={getActionColor(alert.recommendedAction)}
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedAlert(alert);
                      }}
                    >
                      View Details
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!selectedAlert} onOpenChange={() => setSelectedAlert(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>
              {selectedAlert?.userName || `User ${selectedAlert?.userId}`} - Abuse Alert Details
            </DialogTitle>
            <DialogDescription>{selectedAlert?.userEmail}</DialogDescription>
          </DialogHeader>

          {selectedAlert && (
            <div className="space-y-6">
              {/* Abuse Score */}
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Total Abuse Score</span>
                  <span className="text-2xl font-bold text-red-600">{selectedAlert.totalAbuseScore}</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-red-600 h-2 rounded-full"
                    style={{ width: `${Math.min(selectedAlert.totalAbuseScore, 300) / 3}%` }}
                  ></div>
                </div>
              </div>

              {/* Detected Patterns */}
              <div>
                <h3 className="font-semibold mb-3">Detected Patterns</h3>
                <div className="space-y-2">
                  {selectedAlert.patterns.map((pattern, idx) => (
                    <div key={idx} className={`border rounded-lg p-3 ${getSeverityColor(pattern.severity)}`}>
                      <div className="flex items-center gap-2 mb-2">
                        {getPatternIcon(pattern.patternType)}
                        <span className="font-semibold capitalize">
                          {pattern.patternType.replace(/_/g, " ")}
                        </span>
                        <Badge variant="outline" className="ml-auto">
                          {pattern.severity.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="text-sm">
                        <p className="mb-1">
                          <strong>Score:</strong> {pattern.abuseScore}
                        </p>
                        <p>
                          <strong>Details:</strong> {JSON.stringify(pattern.details, null, 2)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended Action */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="font-semibold text-blue-900 mb-1">Recommended Action</p>
                <p className="text-blue-800 capitalize">{selectedAlert.recommendedAction}</p>
              </div>

              {/* Action Section */}
              <div className="space-y-3 border-t pt-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Block Reason</label>
                  <Textarea
                    placeholder="Enter reason for blocking this user..."
                    value={blockReason}
                    onChange={(e) => setBlockReason(e.target.value)}
                    className="min-h-20"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">Block Duration</label>
                  <select
                    value={blockDuration}
                    onChange={(e) => setBlockDuration(e.target.value as any)}
                    className="w-full border rounded-md px-3 py-2"
                  >
                    <option value="1h">1 Hour</option>
                    <option value="24h">24 Hours</option>
                    <option value="7d">7 Days</option>
                    <option value="permanent">Permanent</option>
                  </select>
                </div>

                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    onClick={handleBlockUser}
                    disabled={isBlocking || !blockReason.trim()}
                  >
                    {isBlocking ? "Blocking..." : "Block User"}
                  </Button>
                  <Button variant="outline" onClick={handleUnblockUser} disabled={isBlocking}>
                    {isBlocking ? "Processing..." : "Unblock User"}
                  </Button>
                  <Button variant="secondary" onClick={handleResetCounters} disabled={isBlocking}>
                    {isBlocking ? "Resetting..." : "Reset Counters"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
